// Collection and search results: opens the filter sheet, submits Sort as soon as it changes, and
// loads the next page's cards by itself as the shopper nears the end of the grid (infinite scroll;
// keyboard users get the "Load more" button instead).
// "Load more" stays a real link: it works without this file, search engines follow it, and it comes
// back as a tap-to-retry button if an automatic load fails.
const REQUEST_TIMEOUT_MS = 15000;
// Start loading about one phone screen before the shopper reaches the end of the grid.
const PRELOAD_MARGIN = '0px 0px 800px 0px';
// How often the scroll position is written down while the shopper scrolls (Safari allows at most
// 100 history writes in 10 seconds).
const SAVE_EVERY_MS = 300;
const sheet = document.getElementById('FacetsSheet');
const autoLink = document.querySelector('[data-load-more]');
const autoWrapper = autoLink?.closest('.load-more');

// Back from a product page must bring the shopper back to the same card. Shopify sends
// Cache-Control: no-store, so the browser reloads this page on Back instead of keeping it, and the
// pages infinite scroll added are gone. So this page's history entry remembers how many pages were on
// screen and the scroll position (history.state survives that reload; the URL stays as it is), and
// the next load puts both back. The browser's own scroll restore would jump before the pages are back.
let pagesShown = 1;
let restoring = false;
history.scrollRestoration = 'manual';

function remember() {
  if (restoring) return;
  const state = history.state && typeof history.state === 'object' ? history.state : {};
  try {
    history.replaceState({ ...state, facets: { pages: pagesShown, y: Math.round(window.scrollY) } }, '');
  } catch (error) {
    // Safari's write limit: the next scroll or pagehide writes it again.
    console.error(error);
  }
}

/**
 * Appends the next page's cards to the grid.
 * @param {HTMLAnchorElement} link - the "Load more" link; its href is the next page
 * @param {{ fromTap: boolean }} options - a tap moves focus to the new cards and, on failure, opens the next page
 * @returns {Promise<boolean>} true when cards were added
 */
async function loadMore(link, { fromTap }) {
  const grid = document.querySelector('[data-product-grid]');
  if (!grid || link.getAttribute('aria-busy') === 'true') return false;
  link.setAttribute('aria-busy', 'true');
  try {
    // AbortSignal.timeout is missing on some older phones: then the request runs without a timeout.
    const response = await fetch(link.href, { signal: AbortSignal.timeout?.(REQUEST_TIMEOUT_MS) });
    if (!response.ok) throw new Error(`Load more failed (${response.status})`);
    const nextPage = new DOMParser().parseFromString(await response.text(), 'text/html');
    const newCards = [...(nextPage.querySelector('[data-product-grid]')?.children || [])];
    grid.append(...newCards);
    const nextLink = nextPage.querySelector('[data-load-more]');
    if (nextLink) link.setAttribute('href', nextLink.getAttribute('href'));
    else link.closest('.load-more')?.remove();
    pagesShown += 1;
    remember();
    // Cards from the next page start at quantity 0; assets/cart.js shows their real quantities.
    document.dispatchEvent(new CustomEvent('products:appended'));
    // After a tap, keyboard and screen-reader users continue from the first new card. Automatic loads
    // leave focus alone: moving it would make the page jump while the shopper scrolls.
    if (fromTap) newCards[0]?.querySelector('.card__title')?.focus();
    return true;
  } catch (error) {
    console.error(error);
    if (fromTap) window.location.assign(link.href);
    return false;
  } finally {
    link.removeAttribute('aria-busy');
  }
}

/**
 * Loads pages 2..N again, one after another, keeping the shopper at the saved position as far as the
 * page reaches: they watch the spinner at the end of the grid until the card they left is back. A
 * failed load stops there and shows "Load more" to retry. Once the shopper scrolls, the page stops
 * moving them.
 * @param {{ pages: number, y: number }} saved - from history.state
 */
async function restore({ pages, y }) {
  restoring = true;
  let follow = true;
  const stopFollowing = () => {
    follow = false;
  };
  for (const type of ['wheel', 'touchstart', 'keydown']) {
    window.addEventListener(type, stopFollowing, { once: true, passive: true });
  }
  const scrollBack = () => follow && window.scrollTo(0, y);
  scrollBack();
  for (let page = 2; page <= pages && autoLink?.isConnected; page += 1) {
    if (!(await loadMore(autoLink, { fromTap: false }))) {
      autoWrapper?.removeAttribute('data-auto');
      break;
    }
    scrollBack();
  }
  restoring = false;
  remember();
}

document.addEventListener('click', (event) => {
  if (event.target.closest('[data-facets-open]')) {
    sheet?.showModal();
    return;
  }
  // A tap on the dialog element itself (not its content) is a tap on the backdrop.
  if (sheet && (event.target === sheet || (sheet.contains(event.target) && event.target.closest('[data-sheet-close]')))) {
    sheet.close();
    return;
  }
  const more = event.target.closest('[data-load-more]');
  if (more) {
    event.preventDefault();
    loadMore(more, { fromTap: true });
  }
});

// Infinite scroll. The link is the trigger: while it is within PRELOAD_MARGIN of the screen, the next
// page loads. Older browsers without IntersectionObserver keep the plain "Load more" button.
function startInfiniteScroll() {
  if (!autoLink?.isConnected || !autoWrapper?.hasAttribute('data-auto')) return;
  const observer = new IntersectionObserver(
    async ([entry]) => {
      if (!entry.isIntersecting) return;
      observer.unobserve(autoLink);
      // Keyboard focus in the grid: new cards would keep pushing the bottom nav out of Tab's reach, so
      // the "Load more" button shows instead, for the rest of this visit. Mouse and touch never set
      // :focus-visible on a card, so they keep loading by themselves.
      if (document.querySelector('[data-product-grid] :focus-visible')) {
        autoWrapper.removeAttribute('data-auto');
        return;
      }
      const loaded = await loadMore(autoLink, { fromTap: false });
      // A failed load shows the button again, so the shopper can tap to retry.
      if (!loaded) autoWrapper.removeAttribute('data-auto');
      // Observing again reports where the link is now, so a short page keeps loading until it fills.
      else if (autoLink.isConnected) observer.observe(autoLink);
    },
    { rootMargin: PRELOAD_MARGIN }
  );
  observer.observe(autoLink);
}

if ('IntersectionObserver' in window) autoWrapper?.setAttribute('data-auto', '');

let saveTimer;
window.addEventListener(
  'scroll',
  () => {
    if (!saveTimer) {
      saveTimer = setTimeout(() => {
        saveTimer = null;
        remember();
      }, SAVE_EVERY_MS);
    }
  },
  { passive: true }
);
window.addEventListener('pagehide', remember);

const saved = history.state?.facets;
if (Number.isInteger(saved?.pages) && saved.pages > 0 && Number.isFinite(saved.y)) {
  restore(saved).then(startInfiniteScroll);
} else {
  startInfiniteScroll();
}

document.addEventListener('change', (event) => {
  event.target.closest('[data-facets-sort-select]')?.form.requestSubmit();
});
